import { Modal, Spin, Steps, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useReducer, useState } from "react";
import { Language } from "redux/actions";
import { UTILS } from "utils";
import { API } from "api";
import { AiInfluencerBreadCrumb } from "features/menu/item.routes";
import { errorHandler } from "api/api/errorHandler";
import TailwindButton from "components/Button";
import { stepsEnum } from "./options";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import StepFour from "./StepFour";

/**
 * Modal wrapper component for AI Influencer generation flow
 * @param {Object} props - Component props including open state and close handler
 */
export const AIInfluencerModal = (props) => {
  const { open, handleClose, ...rest } = props;
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={t("Generate AI Influencer")}
      width={window?.innerWidth - 100}
      centered
    >
      <AIInfluencer {...rest} />
    </Modal>
  );
};

/**
 * Reducer function to manage steps state and data
 * @param {Object} state - Current state of steps
 * @param {Object} action - Action object with type and payload
 */
const stepsReducer = (state, action) => {
  switch (action.type) {
    case "SET_STEPS":
      return { ...state, ...action.payload };
    case "UPDATE_STEP":
      return {
        ...state,
        [action.step]: { ...state[action.step], ...action.payload },
      };
    default:
      return state;
  }
};

/**
 * Main AI Influencer component that handles the multi-step generation flow
 * @param {Object} props - Component props including itemInfo and itemId
 */
const AIInfluencer = ({ itemInfo: initialItemInfo, itemId: propItemId }) => {
  const dispatch = useDispatch();
  const router = useHistory();
  const { t, i18n } = useTranslation();
  const user = UTILS.getCurrentUser();
  const { integrations } = useSelector((state) => state.SettingsReducer);
  

  const isAiInfluencerEnabled = useMemo(() => {
    return integrations?.some((integration) => integration?.name === "CREATIFY_AI") || false;
  }, [integrations]);

  const initialSteps = {
    stepOne: {
      titleKey: "Step 1",
      descriptionKey: "Item Info",
      title: "Step 1",
      description: "Item Info",
      status: "process",
    },
    stepTwo: {
      titleKey: "Step 2",
      descriptionKey: "AI Influencer Settings",
      title: "Step 2",
      description: "AI Influencer Settings",
      status: "wait",
    },
    stepThree: {
      titleKey: "Step 3",
      descriptionKey: "Avatar Selection",
      title: "Step 3",
      description: "Avatar Selection",
      status: "wait",
    },
    stepFour: {
      titleKey: "Step 4",
      descriptionKey: "Preview & Save",
      title: "Step 4",
      description: "Preview & Save",
      status: "wait",
    },
  };
  
  const [stepsInfo, dispatchSteps] = useReducer(stepsReducer, initialSteps);
  const [loading, setLoading] = useState(false);
  const [itemDetails, setItemDetails] = useState(initialItemInfo);
  const [currentStep, setCurrentStep] = useState(null);
  const [AIInfluencerData, setAIInfluencerData] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);

  const itemId = propItemId ?? router?.location?.state?.itemId;

  /**
   * Sets the breadcrumb navigation with optional title
   * @param {string} title - Optional title to add to breadcrumb
   */
  const handleSetBreadCrumb = (title) => {
    const breadcrumbs = [...AiInfluencerBreadCrumb];
    if (title) breadcrumbs.push({ label: title });
    dispatch(Language.setPageBreadCrumb(breadcrumbs));
  };

  /**
   * Fetches item details from API using item ID
   * @param {string} itemId - The ID of the item to fetch details for
   */
  const getItemDetails = async (itemId) => {
    const result = await API.GET_ITEM_INFO({
      itemId,
      token: user?.token,
      brandId: user?.brandId,
      userId: user?.userId,
    });
    if (result?.success === 1) {
      if(result?.data?.aiInfluencerAd?.video) setConfirmModal(true)
      setItemDetails(result?.data);
      setCurrentStep(result?.data?.aiInfluencerAd?.video ? stepsEnum.stepZero :stepsEnum.stepOne);
    }
  };

  /**
   * Retrieves cached data for the item and updates steps accordingly
   * @param {string} itemId - The ID of the item to get cached data for
   * @returns {Object|null} Cached data object or null if not found
   */
  const getCachedData = async (itemId) => {
    try {
      const cachedData = await API.GET_TRANSIENT_CACHED_DATA({
        itemId,
        token: user?.token,
        brandId: user?.brandId,
        type: "creatifyApi",
      });
      if(!cachedData?.data || Object.keys(cachedData?.data?.payload).length <= 0) return null;
      const {
        title,
        description,
        image_urls = [],
        video_urls = [],
      } = cachedData?.data?.payload?.stepOne?.link || {};

      setItemDetails({
        baseLanguage: { title, description },
        itemImageURLs: [...video_urls, ...image_urls],
        isCached: true,
      });
      setAIInfluencerData(cachedData?.data);
      const payload = cachedData?.data?.payload || {};
      const allSteps = Object.keys(stepsInfo);
      
      let firstInvalidStep = null;
      let lastValidStep = null;
      let processStepSet = false;
      
      const stepsPayload = allSteps.reduce((acc, step) => {
        const stepData = payload[step];
        const isValid = stepData && Object.keys(stepData).length > 0;
        if (isValid) {
          acc[step] = {
            ...stepsInfo[step],
            status: "finish",
          };
          lastValidStep = step;
        } else {
          if (!firstInvalidStep) firstInvalidStep = step;
          acc[step] = {
            ...stepsInfo[step],
            status: !processStepSet ? "process" : "wait",
          };
          processStepSet = true;
        }
        return acc;
      }, {});
      
      dispatchSteps({
        type: "SET_STEPS",
        payload: stepsPayload,
      });
      
      setCurrentStep(firstInvalidStep ?? lastValidStep ?? "step1");
      
      return cachedData;
    } catch {
      return null;
    }
  };

  /**
   * Initializes component data by checking for cached data or fetching item details
   */
  const getInitialData = async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const cached = await getCachedData(itemId);
      if (!cached) await getItemDetails(itemId);
    } catch (err) {
      console.error(err);
      UTILS.errorNotification(errorHandler(err?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!isAiInfluencerEnabled || !router?.location?.state?.isAiInfluencerEnabled) return
    handleSetBreadCrumb(null);
    if (!itemId) {
      router.goBack();
    } else if (initialItemInfo) {
      setItemDetails(initialItemInfo);
      setCurrentStep("step1");
    } else {
      getInitialData();
    }
  }, [itemId]);

  useEffect(() => {
    if (itemDetails) {
      handleSetBreadCrumb(itemDetails?.baseLanguage?.title);
    }
  }, [itemDetails]);

  useEffect(() => {
    Object.entries(stepsInfo).forEach(([stepKey, stepValue]) => {
      dispatchSteps({
        type: "UPDATE_STEP",
        step: stepKey,
        payload: {
          title: t(stepValue.titleKey),
          description: t(stepValue.descriptionKey),
        },
      });
    });
  }, [i18n?.language]);

  const saveTransientCachedData = async (payload) => {
    try {
      await API.CREATE_UPDATE_TRANSIENT_CACHED_DATA({
        ...payload,
      })
    }
    catch (err) {
      console.error(err);
    }
  }

  /**
   * Handles navigation to the next step and updates step status
   * @param {string} nextStepKey - The key of the next step to navigate to
   * @param {Object} currentStepData - Data from the current step
   */
  const handleNextStep = async (nextStepKey, currentStepData = {}, saveCachedData = false, reset=false) => {
    try {
      setLoading(true);

      if (Object.keys(currentStepData).length <= 0)
        return
      
      const tempPayload = {
        ...AIInfluencerData,
        type: "creatifyApi",
        payload: {
          ...AIInfluencerData?.payload,
          [currentStep]: currentStepData,

        }
      }
      if (saveCachedData && !reset) {
        await saveTransientCachedData(tempPayload);
      }
      dispatchSteps(reset ? initialSteps :{
        type: "SET_STEPS",
        payload: {
          ...stepsInfo,
          [currentStep]: {
            ...stepsInfo[currentStep],
            status: "finish",
          },
          [nextStepKey]: {
            ...stepsInfo[nextStepKey],
            status: "process",
          },
        },
      });
      setAIInfluencerData(reset ? {}:tempPayload);
      setCurrentStep(reset ? stepsEnum.stepOne :nextStepKey);
     

    } catch (err) {
      console.error(err);
      UTILS.errorNotification(errorHandler(err?.error?.message));
    } finally {
      setLoading(false);
    }

  };

  /**
   * Handles step change when user clicks on a step in the Steps component
   * @param {number} current - The index of the step to navigate to
   */
  const handleStepChange = (current) => {
    setCurrentStep(Object.keys(stepsInfo)[current]);
  };

  const StepComponents = useMemo(
    () => ({
      [stepsEnum.stepOne]: StepOne,
      [stepsEnum.stepTwo]: StepTwo,
      [stepsEnum.stepThree]: StepThree,
      [stepsEnum.stepFour]: StepFour,
    }),[]);

  const CurrentStepComponent = StepComponents[currentStep] || (() => null);

  const handleViewExisting = () => {
    setConfirmModal(false)
    setCurrentStep(stepsEnum.stepFour)
    setAIInfluencerData({
      payload: {
        stepOne: {
          id: itemDetails?.aiInfluencerAd?.linkId
        },
        stepFour: {
          video_thumbnail: itemDetails?.aiInfluencerAd?.thumbnail,
          video_output: itemDetails?.aiInfluencerAd?.video,
          status: "done",
          isAlreadyPosted: itemDetails?.aiInfluencerAd?.isAlreadyPosted
        }
      }
    })
    const stepsPayload = initialSteps
    Object.keys(initialSteps).forEach((key) => {
      stepsPayload[key].status = "finish";
    });
    stepsPayload.stepFour.status = "process"
    console.log({stepsPayload})
    dispatchSteps({
      type: "SET_STEPS",
      payload: stepsPayload,
    });
  }

  if(!isAiInfluencerEnabled || !router?.location?.state?.isAiInfluencerEnabled) {
    return router.goBack();
  }

  return (
    <Spin spinning={loading}>
      <div className="p-5">
        <div className="my-5 flex justify-center mx-auto">
          <div style={{ width: "clamp(300px, 50% + 10vw, 80%)" }}>
            <Steps
              current={Object.keys(stepsInfo).findIndex(
                (step) => step === currentStep
              )}
              items={Object.values(stepsInfo)}
              // onChange={handleStepChange}
            />
          </div>
        </div>
        <CurrentStepComponent
          itemId={itemId}
          itemInfo={itemDetails}
          cachedData={AIInfluencerData?.payload}
          loading={loading}
          user={user}
          onNext={handleNextStep}
          setLoading={setLoading}
          setCurrentStep={setCurrentStep}
        />
      </div>
      <Modal
        closable={false}
        open={confirmModal}
        centered
        footer={
          <div className="flex justify-center gap-4">
            <TailwindButton
              outline
              variant="danger"
              text={"View Existing"}
              onClick={handleViewExisting}
            />
            <TailwindButton
              outline
              text={"Create New"}
              onClick={() => {
                setConfirmModal(false);
                setCurrentStep(stepsEnum.stepOne);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-2 justify-center items-center">
          <Typography.Title level={5}>
            {t("AI Influencer for this item has already been created")}
          </Typography.Title>
          <Typography.Title
            level={4}
            type="danger"
            style={{
              margin: 0,
            }}
          >
            {t("Do you want to create a new AI Influencer?")}
          </Typography.Title>
        </div>
      </Modal>
    </Spin>
  );
};

export default AIInfluencer;
